<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { showToast, showLoadingToast, closeToast } from 'vant'
import { api } from '@/api/api'
import { openWxMiniPay } from '@/assets/js/base'

/**
 * 新人红包：对接后端 getNewRedPacket / receiveNewRedPacket
 * - 登录成功或进入首页/我的且已登录时拉取红包信息
 * - payStatus≠3 且 countdown 未过期：展示悬浮窗（未领取：领红包；已领取：去微信确认收款）
 * - 领取接口仅下发 packageInfo，到账需用户在微信内确认，通过 openWxMiniPay(packageInfo) 跳转
 * - 悬浮窗倒计时按秒递减，为 0 时关闭悬浮窗并同步关闭主弹窗
 * - 金额使用后端 packetAmount
 */

const route = useRoute()

/** sessionStorage：本次会话是否已自动弹出过主弹窗 */
const KEY_MAIN_SHOWN_SESSION = 'newRedPacketMainShownSession'
/** sessionStorage：用户是否点关闭最小化到悬浮窗 */
const KEY_MINIMIZED = 'newRedPacketMinimized'

const showMainPopup = ref(false)
const showSuccessPopup = ref(false)
const showFloating = ref(false)
/** 打款成功提示弹窗：用于拦截重复确认收款时的稳定提示 */
const showPaidSuccessPopup = ref(false)

/** 后端是否允许展示红包模块 */
const redPacketEnabled = ref(false)
/** 是否已领取 */
const received = ref(false)
/** 是否绑定微信 */
const bindWechat = ref(true)
/**
 * 拉起微信确认收款所需的 packageInfo
 * 来源：getNewRedPacket.result.packageInfo 或 receiveNewRedPacket.result
 */
const packageInfo = ref('')
/**
 * 支付状态：1待支付 2支付中 3支付成功 4支付失败 -1支付异常
 * 仅 payStatus===3 视为到账完成，其余在倒计时未结束前可展示「待确认」悬浮入口
 */
const payStatus = ref(1)

const amount = ref('0.3')
const title = computed(() => `恭喜您获得新人现金红包${amount.value}元`)

/** 剩余秒数（后端倒计时字段是毫秒，这里换算为秒后本地递减） */
const countdownRemainingSeconds = ref(0)
let secondTimer = null
/** 防止并发拉取导致弹窗状态抖动 */
let isFetchingRedPacketInfo = false
/** 最近一次登录触发时间（用于忽略紧随其后的重复刷新事件） */
let lastLoginTriggerAt = 0

const claiming = ref(false)
/** 点击「前往微信确认收款」时的状态校验 loading，防止重复点击 */
const checkingWxConfirmStatus = ref(false)
/**
 * 是否已点击过「前往微信确认收款」按钮
 * 仅在该标记为 true 时，onPageResume 才会回源刷新支付状态
 */
const hasTriggeredWxMiniConfirm = ref(false)
/** 保存页面原有 onPageResume，避免覆盖其它页面逻辑 */
let previousOnPageResume = null

/** 是否已微信支付成功（到账完成） */
const isPaySuccess = computed(() => payStatus.value === 3)
/**
 * 未到账且倒计时仍有剩余：允许展示悬浮窗（与后端「活动未结束」对齐）
 */
const shouldShowFloatForPendingPay = computed(
  () => !isPaySuccess.value && countdownRemainingSeconds.value > 0
)

const shouldShowOnCurrentPage = computed(() => {
  const n = route.name
  return n === 'home' || n === 'mine' || n === 'Home' || n === 'Mine'
})

/** 仅在首页/我的且后端允许展示时，渲染红包相关 UI */
const canShowRedPacketUi = computed(() => redPacketEnabled.value && shouldShowOnCurrentPage.value)

/**
 * 将金额格式化为展示字符串（去掉多余小数 0）
 * @param {number|string} val 后端 packetAmount
 */
function formatPacketAmount(val) {
  const num = Number(val)
  if (!Number.isFinite(num)) return '0'
  const s = num.toFixed(2)
  return s.replace(/\.?0+$/, '')
}

/**
 * 将剩余秒数格式化为「X小时XX分XX秒」文案
 * @param {number} sec 剩余秒
 */
function formatCountdownSeconds(sec) {
  if (sec <= 0) return '已失效'
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const pad = (n) => (n < 10 ? `0${n}` : String(n))
  return `${h}小时${pad(m)}分${pad(s)}秒后失效`
}

const countdownText = computed(() => formatCountdownSeconds(countdownRemainingSeconds.value))

/** 停止本地秒级倒计时 */
function stopSecondTick() {
  if (secondTimer) {
    clearInterval(secondTimer)
    secondTimer = null
  }
}

/** 启动本地秒级倒计时（依赖 countdownRemainingSeconds 当前值） */
function startSecondTick() {
  stopSecondTick()
  if (countdownRemainingSeconds.value <= 0) return
  secondTimer = setInterval(() => {
    if (countdownRemainingSeconds.value <= 0) {
      stopSecondTick()
      onCountdownFinished()
      return
    }
    countdownRemainingSeconds.value -= 1
    if (countdownRemainingSeconds.value <= 0) {
      stopSecondTick()
      onCountdownFinished()
    }
  }, 1000)
}

/**
 * 倒计时结束：关闭悬浮窗与主弹窗，并重新拉取一次后端状态
 */
async function onCountdownFinished() {
  showFloating.value = false
  showMainPopup.value = false
  sessionStorage.removeItem(KEY_MINIMIZED)
  try {
    await fetchNewRedPacketInfo({ afterLogin: false, silent: true })
  } catch {
    redPacketEnabled.value = false
  }
}

/**
 * 拉取新用户红包信息
 * @param {{ afterLogin?: boolean, silent?: boolean }} opts afterLogin=登录成功后的首次拉取；silent=失败时不 toast
 */
async function fetchNewRedPacketInfo(opts = {}) {
  // 请求互斥，避免短时间多次触发导致重复动画
  if (isFetchingRedPacketInfo) return
  isFetchingRedPacketInfo = true
  const { afterLogin = false, silent = false } = opts
  const token = localStorage.getItem('token')
  // 有 token 即拉取：登录态可能在非首页路由，仍需先同步后端红包状态
  if (!token) {
    isFetchingRedPacketInfo = false
    return
  }
  try {
    const res = await api.getNewRedPacket({})
    if (res.code !== 200 || !res.result) {
      if (!silent) showToast(res.message || '获取红包信息失败')
      redPacketEnabled.value = false
      showMainPopup.value = false
      showFloating.value = false
      showSuccessPopup.value = false
      stopSecondTick()
      return
    }
    const r = res.result
    if (!r.show) {
      redPacketEnabled.value = false
      showMainPopup.value = false
      showFloating.value = false
      showSuccessPopup.value = false
      stopSecondTick()

      return
    }

    redPacketEnabled.value = true
    received.value = !!r.received
    bindWechat.value = !!r.bingWechat
    packageInfo.value = typeof r.packageInfo === 'string' ? r.packageInfo : ''
    amount.value = formatPacketAmount(r.packetAmount)

    // 解析支付状态（缺省时：已领取按「支付中」对待，未领取按「待支付」）
    const psRaw = r.payStatus
    const psNum = psRaw != null && psRaw !== '' ? Number(psRaw) : NaN
    payStatus.value = Number.isFinite(psNum) ? psNum : received.value ? 2 : 1

    // 后端 countdownSecond 为毫秒，向上取整为秒，避免用户看到倒计时提前结束
    const countdownMs = Math.max(0, Number(r.countdownSecond) || 0)
    countdownRemainingSeconds.value = Math.ceil(countdownMs / 1000)
    startSecondTick()

    // 已领取：主弹窗关闭；未到账时在首页/我的展示悬浮入口（成功引导弹窗打开时不叠悬浮窗）
    if (received.value) {
      showMainPopup.value = false
      sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
      sessionStorage.removeItem(KEY_MINIMIZED)
      const needFloat =
        shouldShowOnCurrentPage.value &&
        !isPaySuccess.value &&
        countdownRemainingSeconds.value > 0 &&
        !showSuccessPopup.value
      showFloating.value = needFloat
      return
    }

    if (afterLogin) {
      sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
      sessionStorage.removeItem(KEY_MINIMIZED)
    }

    // 非首页/我的：只同步数据，不弹出层（进入 Tab 后再由路由 watch 展示）
    if (!shouldShowOnCurrentPage.value) {
      showMainPopup.value = false
      showFloating.value = false
      return
    }

    const minimized = sessionStorage.getItem(KEY_MINIMIZED) === '1'
    const mainShown = sessionStorage.getItem(KEY_MAIN_SHOWN_SESSION) === '1'

    if (minimized) {
      showFloating.value = true
      showMainPopup.value = false
      return
    }

    if (afterLogin || !mainShown) {
      showMainPopup.value = true
      showFloating.value = false
      sessionStorage.setItem(KEY_MAIN_SHOWN_SESSION, '1')
      return
    }

    showMainPopup.value = false
    showFloating.value = false
  } catch (e) {
    if (!silent) showToast('网络异常，请稍后重试')
    console.error('getNewRedPacket', e)
  } finally {
    isFetchingRedPacketInfo = false
  }
}

/** 打开主弹窗（从悬浮窗进入时清除最小化标记） */
function openMainPopup() {
  if (!canShowRedPacketUi.value || received.value) return
  sessionStorage.removeItem(KEY_MINIMIZED)
  showMainPopup.value = true
  showFloating.value = false
}

/**
 * 领取红包：调用 receiveNewRedPacket
 * 成功返回的 result 即为 packageInfo；到账需在微信内确认，不停止本地倒计时
 */
async function claimRedPacket() {
  if (claiming.value || received.value || !redPacketEnabled.value) return
  claiming.value = true
  try {
    const res = await api.receiveNewRedPacket({})
    // 后端领取成功码可能为 200 或 0
    const ok = res.code === 200 || res.code === 0
    if (ok) {
      const pkg = typeof res.result === 'string' ? res.result : ''
      if (pkg) {
        packageInfo.value = pkg
      }
      showMainPopup.value = false
      received.value = true
      payStatus.value = 2
      sessionStorage.removeItem(KEY_MINIMIZED)
      sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
      setTimeout(() => {
        showSuccessPopup.value = true
        const tip = bindWechat.value
          ? '领取成功，请前往微信内确认收款'
          : '领取成功，请先绑定微信后再到微信确认收款'
        showToast(tip)
      }, 200)
      // 同步后端倒计时与 payStatus（保持倒计时运行，不在此处 stopSecondTick）
      void fetchNewRedPacketInfo({ afterLogin: false, silent: true })
    } else {
      showToast(res.message || '领取失败，请稍后重试')
    }
  } catch (e) {
    console.error('receiveNewRedPacket', e)
    showToast('网络异常，请稍后重试')
  } finally {
    claiming.value = false
  }
}

/** 关闭主弹窗并最小化到悬浮窗 */
function closeMainToFloat() {
  if (!redPacketEnabled.value || received.value) return
  sessionStorage.setItem(KEY_MINIMIZED, '1')
  showMainPopup.value = false
  showFloating.value = true
}

function closeSuccessPopup() {
  showSuccessPopup.value = false
  // 关闭引导弹窗后，若仍需去微信确认收款，继续展示悬浮入口
  if (
    received.value &&
    payStatus.value !== 3 &&
    countdownRemainingSeconds.value > 0 &&
    shouldShowOnCurrentPage.value
  ) {
    showFloating.value = true
  }
}

/** 关闭打款成功提示弹窗 */
function closePaidSuccessPopup() {
  showPaidSuccessPopup.value = false
}

/**
 * 唤起微信确认收款（原生 wxOpenBusiness）
 */
async function handleOpenWxMiniConfirm() {
  // 正在校验时不重复触发，避免重复请求与重复跳转
  if (checkingWxConfirmStatus.value) return
  const pkg = (packageInfo.value || '').trim()
  if (!pkg) {
    showToast('暂无可用的收款信息，请稍后重试或重新进入页面')
    return
  }
  checkingWxConfirmStatus.value = true
  showLoadingToast({
    message: '状态校验中...',
    forbidClick: true,
    duration: 0,
  })
  try {
    // 跳转前先拉取最新状态：若已到账，直接提示并关闭相关弹层
    const res = await api.getNewRedPacket({})
    if (res.code !== 200 || !res.result) {
      showToast(res.message || '状态校验失败，请稍后重试')
      return
    }
    const r = res.result
    const currentReceived = !!r.received
    const psRaw = r.payStatus
    const psNum = psRaw != null && psRaw !== '' ? Number(psRaw) : NaN
    const latestPayStatus = Number.isFinite(psNum) ? psNum : currentReceived ? 2 : 1

    if (latestPayStatus === 3) {
      payStatus.value = 3
      received.value = true
      hasTriggeredWxMiniConfirm.value = false
      showSuccessPopup.value = false
      showFloating.value = false
      showMainPopup.value = false
      // 使用独立弹窗提示打款成功，避免 toast 在弹层切换时一闪而过
      showPaidSuccessPopup.value = true
      return
    }

    // 未到账则继续原流程，等待 onPageResume 后再次回源刷新
    hasTriggeredWxMiniConfirm.value = true
    openWxMiniPay(pkg)
  } catch (e) {
    console.error('getNewRedPacket before wx confirm', e)
    showToast('网络异常，请稍后重试')
  } finally {
    checkingWxConfirmStatus.value = false
    closeToast()
  }
}

/**
 * 原生 onResume 回调到 H5：仅在用户点过确认收款按钮后刷新红包状态
 * 若已支付成功（payStatus===3），自动关闭引导弹窗与悬浮窗
 */
async function onPageResume() {
  if (typeof previousOnPageResume === 'function') {
    previousOnPageResume()
  }
  if (!hasTriggeredWxMiniConfirm.value) return
  await fetchNewRedPacketInfo({ afterLogin: false, silent: true })
  if (payStatus.value === 3) {
    showSuccessPopup.value = false
    showFloating.value = false
    hasTriggeredWxMiniConfirm.value = false
  }
}

/** 悬浮窗副文案：未领取与已领取待确认区分 */
const floatingActionLabel = computed(() => (received.value ? '去微信确认' : '点击领取'))

/**
 * 点击悬浮窗：未领取进入主弹窗；已领取但未到账则打开引导弹窗以便跳转微信
 */
function openFromFloating() {
  if (!canShowRedPacketUi.value || !shouldShowFloatForPendingPay.value) return
  if (received.value) {
    showSuccessPopup.value = true
    return
  }
  openMainPopup()
}

/** 全局登录成功：清会话标记后拉接口并优先弹主窗 */
function onAuthStateChanged(event) {
  const detail = event?.detail || {}
  if (detail.type === 'login') {
    // 记录登录刷新时间，防止后续立即到来的重复刷新再次触发弹窗动画
    lastLoginTriggerAt = Date.now()
    setTimeout(() => {
      void fetchNewRedPacketInfo({ afterLogin: true, silent: false })
    }, 600)
    return
  }
  if (detail.type === 'logout') {
    redPacketEnabled.value = false
    received.value = false
    payStatus.value = 1
    packageInfo.value = ''
    hasTriggeredWxMiniConfirm.value = false
    showMainPopup.value = false
    showFloating.value = false
    showSuccessPopup.value = false
    showPaidSuccessPopup.value = false
    stopSecondTick()
    sessionStorage.removeItem(KEY_MINIMIZED)
    sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
  }
}

function onNewcomerRewardRefresh() {
  // 登录后会连续触发多个刷新事件，这里短暂节流避免动画重复播放
  if (Date.now() - lastLoginTriggerAt < 1200) return
  if (localStorage.getItem('token')) {
    void fetchNewRedPacketInfo({ afterLogin: false, silent: true })
  }
}

watch(
  () => route.name,
  () => {
    if (!shouldShowOnCurrentPage.value) {
      showMainPopup.value = false
      showSuccessPopup.value = false
      showFloating.value = false
      return
    }
    if (localStorage.getItem('token')) {
      void fetchNewRedPacketInfo({ afterLogin: false, silent: true })
    }
  }
)

onMounted(() => {
  // 页面刷新初始化时清理会话弹出标记，确保刷新后可重新自动弹主红包
  sessionStorage.removeItem(KEY_MAIN_SHOWN_SESSION)
  // 挂载原生 onResume 回调：先执行页面既有回调，再刷新红包状态
  previousOnPageResume = typeof window.onPageResume === 'function' ? window.onPageResume : null
  window.onPageResume = onPageResume
  window.addEventListener('auth-state-changed', onAuthStateChanged)
  window.addEventListener('newcomer-reward-refresh', onNewcomerRewardRefresh)
  if (localStorage.getItem('token')) {
    void fetchNewRedPacketInfo({ afterLogin: false, silent: true })
  }
})

onUnmounted(() => {
  stopSecondTick()
  // 组件卸载时恢复原页面 onPageResume，避免影响其它页面逻辑
  if (window.onPageResume === onPageResume) {
    window.onPageResume = previousOnPageResume
  }
  window.removeEventListener('auth-state-changed', onAuthStateChanged)
  window.removeEventListener('newcomer-reward-refresh', onNewcomerRewardRefresh)
})

defineExpose({
  fetchNewRedPacketInfo,
  openMainPopup,
})
</script>

<template>
  <div class="newcomer-red-packet-wrapper">
    <!-- 主红包弹窗 - 红色主题，居中，不可点击蒙层关闭 -->
    <van-overlay 
      :show="canShowRedPacketUi && showMainPopup && !showSuccessPopup" 
      :z-index="3000"
      class="red-packet-overlay"
      @click="() => {}"
    >
      <div class="popup-container" @click.stop>
        <div class="red-packet-popup">
          <!-- 关闭按钮 - 右上角X，最小化到悬浮窗 -->
          <div class="close-btn" @click="closeMainToFloat">✕</div>
          
          <!-- 红包头部装饰 -->
          <div class="packet-header">
            <div class="ribbon">新人专享</div>
            <div class="packet-icon">🧧</div>
          </div>
          
          <!-- 主标题 -->
          <div class="main-title">{{ title }}</div>
          
          <!-- 金额突出展示 - 大字红色 -->
          <div class="amount-container">
            <span class="amount-symbol">¥</span>
            <span class="amount-value">{{ amount }}</span>
            <span class="amount-unit">元</span>
          </div>
          
          <!-- 倒计时：与后端 countdownSecond 同步，按秒递减 -->
          <div v-if="countdownRemainingSeconds > 0" class="countdown-section">
            <div class="countdown-label">⏰ 限时领取</div>
            <div class="countdown-time">{{ countdownText }}</div>
            <div class="countdown-hint">未领取将自动失效</div>
          </div>
          
          <!-- 立即领取按钮 - 醒目红色渐变 -->
          <button class="claim-btn" type="button" :disabled="claiming" @click="claimRedPacket">
            {{ claiming ? '领取中…' : '立即领取' }}
          </button>
          
          <!-- 底部说明：到账需在微信内确认 -->
          <div class="bottom-hint">领取后请前往微信完成确认收款，到账后可在微信零钱查看</div>
        </div>
      </div>
    </van-overlay>

    <!-- 领取成功引导弹窗：款项需在微信内确认，非即时到账 -->
    <!-- 成功态不依赖 redPacketEnabled，避免领取后接口 show=false 导致弹窗闪退 -->
    <van-overlay 
      :show="shouldShowOnCurrentPage && showSuccessPopup" 
      :z-index="3100"
      class="success-overlay"
      @click="() => {}"
    >
      <div class="popup-container" @click.stop>
        <div class="success-popup">
          <!-- 关闭按钮 -->
          <div class="close-btn success-close" @click="closeSuccessPopup">✕</div>
          
          <!-- 成功图标：领取资格已占用，后续为引导去 -->
          <div class="success-icon" aria-hidden="true">
            <svg viewBox="0 0 64 64" class="success-icon-svg" role="img">
              <circle cx="32" cy="32" r="30" class="success-icon-circle" />
              <path d="M20 33L28 41L45 24" class="success-icon-check" />
            </svg>
          </div>
          <div class="success-title">领取成功</div>
          <div class="success-amount">{{ amount }}元</div>
          <div class="success-desc">
            新人红包已领取，请在微信内点击确认后，现金才会发放至微信零钱。<br>
            <template v-if="!bindWechat">
              当前未绑定微信，请先完成绑定后再前往微信确认。
            </template>
          </div>
          
          <!-- 引导说明 -->
          <div class="wechat-notice">
            <div class="wechat-icon">📲</div>
            <div class="notice-text">点击下方按钮跳转微信完成确认收款</div>
          </div>
          
          <button
            class="wx-mini-primary-btn"
            type="button"
            :disabled="checkingWxConfirmStatus"
            @click="handleOpenWxMiniConfirm"
          >
            {{ checkingWxConfirmStatus ? '状态校验中…' : '前往微信确认收款' }}
          </button>
          <button class="wx-mini-secondary-btn" type="button" @click="closeSuccessPopup">
            稍后处理
          </button>
        </div>
      </div>
    </van-overlay>

    <!-- 打款成功提示弹窗：用于提示已到账，避免重复确认收款 -->
    <van-overlay
      :show="shouldShowOnCurrentPage && showPaidSuccessPopup"
      :z-index="3200"
      class="paid-success-overlay"
      @click="() => {}"
    >
      <div class="popup-container" @click.stop>
        <div class="paid-success-popup">
          <!-- 关闭按钮 -->
          <div class="close-btn paid-success-close" @click="closePaidSuccessPopup">✕</div>

          <div class="paid-success-icon" aria-hidden="true">💰</div>
          <div class="paid-success-title">打款成功</div>
          <div class="paid-success-amount">{{ amount }}元已到账</div>
          <div class="paid-success-desc">
            您已经领取成功，无需重复确认收款。<br>
            可前往微信零钱查看到账明细。
          </div>

          <button class="paid-success-btn" type="button" @click="closePaidSuccessPopup">
            我知道了
          </button>
        </div>
      </div>
    </van-overlay>

    <!-- 悬浮窗：未领取=领红包；已领取待确认=去微信（payStatus≠3 且倒计时未结束） -->
    <div 
      v-if="canShowRedPacketUi && showFloating && shouldShowFloatForPendingPay && !showSuccessPopup" 
      class="floating-red-packet"
      @click="openFromFloating"
    >
      <div class="float-packet">
        <div class="float-amount">新人红包</div>
        <div class="float-countdown">{{ countdownText }}</div>
        <div class="float-label">{{ floatingActionLabel }}</div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* 新人红包UI样式 - 红色主题，吸引人设计，符合移动端375px基准 */
.newcomer-red-packet-wrapper {
  position: relative;
  z-index: 2000;
}

/* 蒙层样式：使用 Vant 默认蒙层，不自定义背景色 */
.red-packet-overlay,
.success-overlay,
.paid-success-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
}

/* 通用弹窗容器 */
.popup-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: auto;
  min-height: 100%;
  padding: 12px;
  width: 100%;
  max-width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

/* 主红包弹窗 - 红色红包主题，吸引人渐变和阴影 */
.red-packet-popup {
  background: linear-gradient(145deg, #ff4d4f 0%, #e60000 50%, #c41e3a 100%);
  border-radius: 20px;
  width: 100%;
  max-width: 280px;
  padding: 22px 14px 24px;
  position: relative;
  box-shadow: 0 20px 40px rgba(220, 20, 60, 0.4),
              0 0 0 8px rgba(255, 255, 255, 0.2) inset;
  color: white;
  text-align: center;
  overflow: hidden;
  animation: popupBounce 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes popupBounce {
  0% { transform: scale(0.6); opacity: 0; }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); opacity: 1; }
}

/* 关闭按钮 */
.close-btn {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 24px;
  height: 24px;
  background: rgba(255,255,255,0.25);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: white;
  cursor: pointer;
  z-index: 10;
  border: 2px solid rgba(255,255,255,0.5);
  transition: all 0.2s;
  
  &:active {
    background: rgba(255,255,255,0.4);
    transform: scale(0.9);
  }
}

.success-close {
  background: rgba(0,0,0,0.1);
  border-color: rgba(255,255,255,0.6);
  color: #fff;
}

.paid-success-close {
  background: rgba(0, 0, 0, 0.12);
  border-color: rgba(255, 255, 255, 0.6);
  color: #fff;
}

/* 头部装饰 */
.packet-header {
  position: relative;
  margin-bottom: 10px;
}

.ribbon {
  position: absolute;
  top: -25px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(90deg, #ffd700, #ffaa00);
  color: #c41e3a;
  font-size: 14px;
  font-weight: 700;
  padding: 2px 12px;
  border-radius: 20px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.2);
  white-space: nowrap;
  letter-spacing: 1px;
}

.packet-icon {
  font-size: 48px;
  margin: 30px 0 4px;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.3));
  animation: packetShake 2s infinite ease-in-out;
}

@keyframes packetShake {
  0%, 100% { transform: rotate(-8deg); }
  50% { transform: rotate(8deg); }
}

/* 主标题 */
.main-title {
  font-size: 14px;
  font-weight: 700;
  margin-bottom: 4px;
  text-shadow: 0 2px 8px rgba(0,0,0,0.3);
  line-height: 1.4;
}

/* 金额展示 - 大字突出 */
.amount-container {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin: 8px 0 10px;
  font-weight: 900;
  text-shadow: 0 4px 15px rgba(0,0,0,0.4);
}

.amount-symbol {
  font-size: 32px;
  margin-right: 4px;
  opacity: 0.9;
}

.amount-value {
  font-size: 56px;
  line-height: 1;
  color: #fff;
  font-weight: 600;
  letter-spacing: -4px;
  text-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
}

.amount-unit {
  font-size: 22px;
  margin-left: 4px;
  align-self: flex-end;
  margin-bottom: 8px;
  font-weight: 700;
}

/* 倒计时区域 */
.countdown-section {
  background: rgba(255,255,255,0.15);
  border-radius: 12px;
  padding: 10px 8px;
  margin: 10px 0 12px;
  border: 1px solid rgba(255,255,255,0.3);
}

.countdown-label {
  font-size: 12px;
  opacity: 0.9;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.countdown-time {
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 2px;
  margin-bottom: 4px;
  color: #fff9c4;
}

.countdown-hint {
  font-size: 11px;
  opacity: 0.85;
}

/* 领取按钮 */
.claim-btn {
  width: 100%;
  height: 44px;
  background: linear-gradient(90deg, #ffd700, #ffaa00, #ffd700);
  color: #c41e3a;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 700;
  margin-top: 6px;
  box-shadow: 0 8px 25px rgba(255, 215, 0, 0.5);
  cursor: pointer;
  transition: all 0.2s;
  text-shadow: 0 1px 2px rgba(255,255,255,0.8);
  
  &:active {
    transform: scale(0.96);
    box-shadow: 0 4px 15px rgba(255, 215, 0, 0.6);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
}

.bottom-hint {
  margin-top: 12px;
  font-size: 11px;
  opacity: 0.8;
  line-height: 1.4;
}

/* 成功弹窗 - 微信收款成功风格 */
.success-popup {
  background: white;
  border-radius: 18px;
  width: 100%;
  max-width: 272px;
  padding: 28px 16px 20px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.4);
  position: relative;
  animation: successPop 0.4s ease;
}

/* 打款成功弹窗：绿色成功态，强调已到账且不可重复确认 */
.paid-success-popup {
  background: linear-gradient(165deg, #2fb669 0%, #1f9d57 100%);
  border-radius: 18px;
  width: 100%;
  max-width: 272px;
  padding: 30px 16px 22px;
  text-align: center;
  box-shadow: 0 25px 50px -12px rgb(0 0 0 / 0.4);
  position: relative;
  animation: successPop 0.35s ease;
  color: #fff;
}

.paid-success-icon {
  font-size: 50px;
  line-height: 1;
  margin-bottom: 10px;
  filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.25));
}

.paid-success-title {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 8px;
}

.paid-success-amount {
  font-size: 30px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 10px;
}

.paid-success-desc {
  font-size: 13px;
  line-height: 1.6;
  opacity: 0.96;
  margin-bottom: 16px;
}

.paid-success-btn {
  width: 100%;
  height: 42px;
  border: none;
  border-radius: 999px;
  background: #fff;
  color: #1f9d57;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;

  &:active {
    transform: scale(0.98);
  }
}

@keyframes successPop {
  0% { transform: scale(0.7); opacity: 0; }
  100% { transform: scale(1); opacity: 1; }
}

.success-icon {
  width: 56px;
  height: 56px;
  margin: 0 auto;
  margin-bottom: 10px;
  animation: successCheck 0.6s ease;
}

.success-icon-svg {
  width: 100%;
  height: 100%;
  display: block;
}

.success-icon-circle {
  fill: #52c41a;
  filter: drop-shadow(0 4px 10px rgba(82, 196, 26, 0.35));
}

.success-icon-check {
  fill: none;
  stroke: #fff;
  stroke-width: 6;
  stroke-linecap: round;
  stroke-linejoin: round;
}

@keyframes successCheck {
  0% { transform: scale(0.5) rotate(-20deg); }
  60% { transform: scale(1.2) rotate(10deg); }
  100% { transform: scale(1) rotate(0); }
}

.success-title {
  font-size: 18px;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 8px;
}

.success-amount {
  font-size: 32px;
  font-weight: 900;
  color: #52c41a;
  margin-bottom: 10px;
  line-height: 1;
}

.success-desc {
  color: #666;
  font-size: 13px;
  line-height: 1.6;
  margin-bottom: 14px;
}

.wechat-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 10px;
  padding: 8px;
  margin-bottom: 14px;
}

.wechat-icon {
  font-size: 20px;
}

.notice-text {
  color: #389e0d;
  font-weight: 600;
  font-size: 12px;
}

.close-success-btn {
  width: 100%;
  height: 40px;
  background: linear-gradient(90deg, #52c41a, #389e0d);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  box-shadow: 0 6px 16px rgba(82, 196, 26, 0.3);
  cursor: pointer;
  
  &:active {
    transform: scale(0.98);
  }
}

/* 引导跳转微信小程序：主按钮 */
.wx-mini-primary-btn {
  width: 100%;
  height: 44px;
  margin-top: 4px;
  background: linear-gradient(90deg, #07c160, #06ae56);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 15px;
  font-weight: 700;
  box-shadow: 0 8px 20px rgba(7, 193, 96, 0.35);
  cursor: pointer;

  &:active {
    transform: scale(0.98);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
  }
}

/* 次要操作：关闭引导，保留悬浮入口稍后处理 */
.wx-mini-secondary-btn {
  width: 100%;
  height: 40px;
  margin-top: 12px;
  background: #f5f5f5;
  color: #595959;
  border: 1px solid #e8e8e8;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:active {
    transform: scale(0.98);
    background: #ebebeb;
  }
}

/* 悬浮窗 - 优化为更像小红包的样子（红色+金边+立体感+纹理） */
.floating-red-packet {
  position: fixed;
  right: 16px;
  bottom: 90px; /* 避开tabbar */
  z-index: 2500;
  cursor: pointer;
  animation: floatAnim 2.8s ease-in-out infinite;
}

@keyframes floatAnim {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-10px) rotate(2deg); }
}

.float-packet {
  width: 68px;
  background: linear-gradient(148deg, #ff3b30 0%, #e60012 48%, #c41e3a 100%);
  border-radius: 18px;
  padding: 10px 6px 9px;
  box-shadow: 
    0 10px 25px rgba(220, 20, 60, 0.55),
    0 0 0 5px rgba(255, 215, 0, 0.45),
    inset 0 6px 10px rgba(255,255,255,0.35),
    inset 0 -4px 8px rgba(0,0,0,0.3);
  text-align: center;
  color: white;
  border: 3.5px solid #ffeb3b;
  position: relative;
  overflow: hidden;
}

/* 轻微红包褶皱纹理 */
.float-packet::before {
  content: '';
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    135deg,
    transparent 0px,
    transparent 3px,
    rgba(255, 255, 255, 0.12) 3px,
    rgba(255, 255, 255, 0.12) 7px
  );
  pointer-events: none;
  border-radius: 15px;
  z-index: 1;
}

.float-icon {
  font-size: 29px;
  margin-bottom: 3px;
  display: block;
  filter: drop-shadow(0 3px 6px rgba(0,0,0,0.4));
  position: relative;
  z-index: 2;
}

.float-amount {
  font-size: 15px;
  font-weight: 700;
  margin-bottom: 1px;
  text-shadow: 0 2px 5px rgba(0,0,0,0.5);
  position: relative;
  z-index: 2;
}

.float-countdown {
  font-size: 10px;
  opacity: 0.95;
  line-height: 1.1;
  margin-top: 5px;
  margin-bottom: 4px;
  position: relative;
  z-index: 2;
}

.float-label {
  font-size: 9px;
  background: linear-gradient(90deg, #ffeb3b, #ffd700);
  color: #c41e3a;
  padding: 1px 7px 2px;
  border-radius: 10px;
  display: inline-block;
  font-weight: 700;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
  position: relative;
  z-index: 2;
}

/* 响应式优化 - 移动端适配 */
@media (max-width: 375px) {
  .popup-container {
    padding: 10px;
  }
}
</style>
